export interface SvpFilter {
  property: string;
  operator: '::' | '<>' | '<' | '>' | '<=' | '>=';
  value: string;
}

type SvpFiltersCastable =
  | SvpFilters
  | SvpFilter[]
  | string[]
  | string
  | undefined;

const stringToFilter = (filter: string): SvpFilter => {
  const filterRegex =
    /^(?<property>[^<>:=]+)(?<operator>(::|<>|<|>|<=|>=))(?<value>[^<>:=]+)$/;
  try {
    return filter.match(filterRegex)!.groups as unknown as SvpFilter;
  } catch {
    throw new Error(`Invalid filter string ${filter}`);
  }
};

const filterToString = ({ property, operator, value }: SvpFilter) => {
  return `${property}${operator}${value}`;
};

export class SvpFilters {
  filters: SvpFilter[];

  constructor() {
    this.filters = [];
  }

  static from(input: SvpFiltersCastable): SvpFilters {
    if (!input) {
      return new SvpFilters();
    }

    if (input instanceof SvpFilters) {
      return SvpFilters.from(input.filters);
    }

    if (typeof input === 'string') {
      return SvpFilters.from(input.split('|').map(stringToFilter));
    }

    const list = new SvpFilters();
    input.forEach((filter) => list.append(filter));

    return list;
  }

  append(filter: SvpFilter | string): SvpFilters {
    if (typeof filter === 'string') {
      return this.append(stringToFilter(filter));
    }

    this.filters = [
      ...this.filters.filter(
        (f) =>
          !(f.property === filter.property && f.operator === filter.operator)
      ),
      filter,
    ];

    return this;
  }

  remove({ property, operator }: Omit<SvpFilter, 'value'>) {
    this.filters = this.filters.filter(
      (f) => !(f.property === property && f.operator === operator)
    );

    return this;
  }

  concat(obj: SvpFiltersCastable) {
    const inputString = `${this.toString()}|${SvpFilters.from(
      obj
    ).toString()}`.replace(/(^\||\|$)/g, '');
    return SvpFilters.from(inputString);
  }

  isEmpty() {
    return this.filters.length < 1;
  }

  toString() {
    return this.filters.map(filterToString).sort().join('|');
  }
}
